import { ContractorType } from "@remi/enums/contractor-type";
import { OrganizationType } from "@remi/enums/organization-type";
import { TaskType } from "@remi/enums/task-type";
import { isOneOf } from "@remi/ts-utils";
import { DEFINITIONS_BY_TYPE } from "@remi/types/tasks/task-type-definitions";

import { hasCapability, type Project, type User } from "../base";
import { type ContractorProfile, workOrder } from "./workOrder";

export type TaskForPermissionCheck = {
  type: TaskType;
  project?: Project;
};

export const canUserReadTaskType = (user: User, taskType: TaskType): boolean =>
  DEFINITIONS_BY_TYPE[taskType].permission.read.some((cap) => hasCapability(user, cap));

const WorkOrderReviewTaskTypes = [TaskType.REVIEW_AND_ACCEPT_WORK_ORDER, TaskType.REVIEW_AND_CLOSE_WORK_ORDER] as const;

const getWorkOrderContractor = (task: TaskForPermissionCheck): ContractorProfile | null => {
  const contractor = task.project?.organizations.find((org) => org.type === OrganizationType.CONTRACTOR)?.organization;
  if (!contractor || !task.project) {
    return null;
  }
  // Cache-only — see comment on classify/src/project.ts:projectUsesRemiLabor.
  return {
    organizationId: contractor.id,
    isRemiLicensedLabor: task.project.contractorTypeFlow === ContractorType.REMI_LICENSED_LABOR,
  };
};

// The specialist capability sits beside the work order gate rather than inside it: `workOrder.update`
// also governs Reject and general work order editing, which the specialist must not gain.
export const canUserReviewWorkOrder = (
  user: User,
  contractor: ContractorProfile,
  project: Pick<Project, "category">,
): boolean => workOrder.update(user, contractor, project) || hasCapability(user, "task.update.workOrderReview");

export const canUserManageWorkOrderForTask = (user: User, task: TaskForPermissionCheck): boolean => {
  const contractor = getWorkOrderContractor(task);
  return contractor !== null && workOrder.update(user, contractor, task.project);
};

// Task-shaped counterpart of canUserReviewWorkOrder. Deliberately kept out of canUserUpdateTask:
// that gate fans out to ~20 endpoints including reassign and reschedule, so granting the specialist
// there would hand them administrative control of these tasks system-wide.
export const canUserReviewWorkOrderForTask = (user: User, task: TaskForPermissionCheck): boolean => {
  const { project } = task;
  if (!project || !isOneOf(task.type, WorkOrderReviewTaskTypes)) {
    return false;
  }
  const contractor = getWorkOrderContractor(task);
  return contractor !== null && canUserReviewWorkOrder(user, contractor, project);
};

export const canUserUpdateTask = (user: User, task: TaskForPermissionCheck): boolean => {
  // `permission.update` is not consulted for work-order review types; they delegate to workOrder.update.
  if (isOneOf(task.type, WorkOrderReviewTaskTypes)) {
    return canUserManageWorkOrderForTask(user, task);
  }
  return DEFINITIONS_BY_TYPE[task.type].permission.update.some((cap) => hasCapability(user, cap));
};
